import React, { useEffect, useMemo, useState } from 'react';
import { UsersRound } from 'lucide-react';
import { ClassSession, getClasses, requestClassJoin } from '../services/classService';

interface UserLiveClassesSectionProps { token: string | null; }
const CLASSES_PER_PAGE = 9;
const localDateTimeFormatter = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'short',
});

const getEndDate = (classSession: ClassSession): Date => new Date(classSession.end_date);

const getAvailability = (classSession: ClassSession): 'upcoming' | 'join' | 'passed' => {
  const now = new Date();
  const classDate = new Date(classSession.class_date);
  if (now > getEndDate(classSession)) return 'passed';
  if (now < classDate) return 'upcoming';
  return 'join';
};

export const UserLiveClassesSection: React.FC<UserLiveClassesSectionProps> = ({ token }) => {
  const [classes, setClasses] = useState<ClassSession[]>([]);
  const [isLoading, setIsLoading] = useState(Boolean(token));
  const [error, setError] = useState('');
  const [activePage, setActivePage] = useState(1);
  const [pastPage, setPastPage] = useState(1);
  const [expiredClass, setExpiredClass] = useState<ClassSession | null>(null);
  const [joiningClassId, setJoiningClassId] = useState<number | null>(null);

  useEffect(() => {
    if (!token) return;
    let isMounted = true;
    const load = async () => {
      try {
        const result = await getClasses(token);
        if (isMounted) setClasses(result);
      } catch (loadError) {
        if (isMounted) setError(loadError instanceof Error ? loadError.message : 'Unable to load live classes.');
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };
    void load();
    return () => { isMounted = false; };
  }, [token]);

  const { activeClasses, upcomingClasses, pastClasses } = useMemo(() => {
    const active: ClassSession[] = [];
    const upcoming: ClassSession[] = [];
    const past: ClassSession[] = [];
    classes.forEach((classSession) => {
      const availability = getAvailability(classSession);
      if (availability === 'join') active.push(classSession);
      else if (availability === 'upcoming') upcoming.push(classSession);
      else past.push(classSession);
    });
    return {
      activeClasses: active.sort((a, b) => new Date(a.class_date).getTime() - new Date(b.class_date).getTime()),
      upcomingClasses: upcoming.sort((a, b) => new Date(a.class_date).getTime() - new Date(b.class_date).getTime()),
      pastClasses: past.sort((a, b) => new Date(b.class_date).getTime() - new Date(a.class_date).getTime()),
    };
  }, [classes]);

  const activeUpcomingClasses = [...activeClasses, ...upcomingClasses];
  const activeTotalPages = Math.max(1, Math.ceil(activeUpcomingClasses.length / CLASSES_PER_PAGE));
  const pastTotalPages = Math.max(1, Math.ceil(pastClasses.length / CLASSES_PER_PAGE));
  const safeActivePage = Math.min(activePage, activeTotalPages);
  const safePastPage = Math.min(pastPage, pastTotalPages);
  const paginatedActiveClasses = activeUpcomingClasses.slice((safeActivePage - 1) * CLASSES_PER_PAGE, safeActivePage * CLASSES_PER_PAGE);
  const paginatedPastClasses = pastClasses.slice((safePastPage - 1) * CLASSES_PER_PAGE, safePastPage * CLASSES_PER_PAGE);

  const joinClass = async (classSession: ClassSession) => {
    if (getAvailability(classSession) === 'passed') {
      setExpiredClass(classSession);
      return;
    }
    if (!token) {
      setError('Please sign in again to join this live class.');
      return;
    }

    const joinWindow = window.open('', '_blank');
    if (joinWindow) joinWindow.opener = null;

    setJoiningClassId(classSession.id);
    setError('');
    try {
      const joinUrl = await requestClassJoin(token, classSession.id);
      if (joinWindow) joinWindow.location.href = joinUrl;
      else window.location.assign(joinUrl);
    } catch (joinError) {
      if (joinWindow) joinWindow.close();
      const message = joinError instanceof Error ? joinError.message : 'Unable to join this live class.';
      if (message.toLowerCase().includes('ended')) setExpiredClass(classSession);
      else setError(message);
    } finally {
      setJoiningClassId(null);
    }
  };

  const renderClassCard = (classSession: ClassSession) => {
    const availability = getAvailability(classSession);
    return (
      <article key={classSession.id} className={`relative overflow-hidden rounded-2xl border p-5 transition-all ${availability === 'join' ? 'border-emerald-200 bg-emerald-50/30 shadow-lg shadow-emerald-500/5' : availability === 'upcoming' ? 'border-zinc-200 bg-white hover:border-amber-400 hover:shadow-lg hover:shadow-amber-500/5' : 'border-zinc-200 bg-zinc-50'}`}>
        <div className="flex items-start justify-between gap-4">
          <span className={`rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${availability === 'join' ? 'bg-emerald-500 text-white' : availability === 'upcoming' ? 'bg-amber-100 text-amber-800' : 'bg-zinc-200 text-zinc-600'}`}>{availability === 'join' ? 'Active now' : availability === 'upcoming' ? 'Upcoming' : 'Completed'}</span>
          <span title="Live meeting" className="relative flex h-11 w-11 items-center justify-center rounded-2xl bg-zinc-950 text-amber-400 shadow-lg shadow-zinc-900/15 ring-1 ring-zinc-800">
            <UsersRound className="h-5 w-5" strokeWidth={2.4} aria-hidden="true" />
            <span className={`absolute -right-0.5 -top-0.5 h-3.5 w-3.5 rounded-full border-2 border-white ${availability === 'join' ? 'bg-emerald-500' : availability === 'upcoming' ? 'bg-amber-400' : 'bg-red-500'}`} aria-hidden="true" />
            <span className="sr-only">Live meeting</span>
          </span>
        </div>
        <h3 className="mt-6 text-xl font-black leading-tight text-zinc-900">{classSession.name}</h3>
        <div className="mt-5 space-y-1.5 border-l-2 border-amber-500 pl-3">
          <p className="text-[10px] font-black uppercase tracking-widest text-zinc-400">Your local schedule</p>
          <p className="text-sm font-bold text-zinc-700"><span className="text-zinc-400">Starts:</span> {localDateTimeFormatter.format(new Date(classSession.class_date))}</p>
          <p className="text-sm font-bold text-zinc-700"><span className="text-zinc-400">Ends:</span> {localDateTimeFormatter.format(new Date(classSession.end_date))}</p>
        </div>
        <div className="mt-6">
          {availability === 'join' ? <button type="button" disabled={joiningClassId === classSession.id} onClick={() => void joinClass(classSession)} className="inline-flex w-full items-center justify-center rounded-xl bg-zinc-950 px-4 py-3 text-xs font-black uppercase tracking-widest text-white transition-colors hover:bg-amber-500 hover:text-zinc-950 disabled:cursor-wait disabled:opacity-60">{joiningClassId === classSession.id ? 'Opening class…' : 'Join live class'}</button>
            : availability === 'upcoming' ? <p className="rounded-xl bg-amber-50 px-3 py-3 text-xs font-bold leading-5 text-amber-800">Join access opens at {localDateTimeFormatter.format(new Date(classSession.class_date))}.</p>
              : <p className="rounded-xl bg-zinc-100 px-3 py-3 text-xs font-bold text-zinc-600">This live class date has passed.</p>}
        </div>
      </article>
    );
  };

  return (
    <section className="rounded-[2rem] border border-zinc-200 bg-white p-6 shadow-xl shadow-zinc-200/40 md:p-8">
      <div className="flex flex-col gap-6 border-b border-zinc-100 pb-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-600">Your schedule</p>
          <h2 className="mt-2 text-3xl font-black tracking-tight text-zinc-950">Live Classes</h2>
          <p className="mt-2 text-sm text-zinc-500">Keep track of your upcoming instructor-led sessions.</p>
        </div>
        <div className="flex gap-3 text-xs font-black uppercase tracking-wide">
          <span className="rounded-xl bg-emerald-50 px-3 py-2 text-emerald-700">Live {activeClasses.length}</span>
          <span className="rounded-xl bg-amber-50 px-3 py-2 text-amber-800">Upcoming {upcomingClasses.length}</span>
        </div>
      </div>

      {error && <p className="mt-6 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}
      <p className={`mt-6 text-xs font-black uppercase tracking-widest ${activeClasses.length > 0 ? 'text-emerald-700' : 'text-amber-600'}`}>{activeClasses.length > 0 ? 'Live now & upcoming classes' : 'Upcoming classes'}</p>

      <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {isLoading ? <p className="text-sm text-zinc-500">Loading live classes…</p> : paginatedActiveClasses.length === 0 ? <p className="text-sm text-zinc-500">No active or upcoming live classes for your plan.</p> : paginatedActiveClasses.map(renderClassCard)}
      </div>

      {activeUpcomingClasses.length > CLASSES_PER_PAGE && (
        <nav className="mt-8 flex items-center justify-center gap-2" aria-label="Live classes pagination">
          <button type="button" disabled={safeActivePage === 1} onClick={() => setActivePage((current) => Math.max(1, current - 1))} className="rounded-lg border border-zinc-200 px-3 py-2 text-xs font-bold text-zinc-600 disabled:cursor-not-allowed disabled:opacity-40">Previous</button>
          {Array.from({ length: activeTotalPages }, (_, index) => index + 1).map((pageNumber) => <button key={pageNumber} type="button" onClick={() => setActivePage(pageNumber)} className={`h-9 w-9 rounded-lg text-xs font-black ${safeActivePage === pageNumber ? 'bg-zinc-950 text-white' : 'text-zinc-600 hover:bg-zinc-100'}`}>{pageNumber}</button>)}
          <button type="button" disabled={safeActivePage === activeTotalPages} onClick={() => setActivePage((current) => Math.min(activeTotalPages, current + 1))} className="rounded-lg border border-zinc-200 px-3 py-2 text-xs font-bold text-zinc-600 disabled:cursor-not-allowed disabled:opacity-40">Next</button>
        </nav>
      )}

      {!isLoading && (
        <div className="mt-12 border-t border-zinc-100 pt-8">
          <p className="text-xs font-black uppercase tracking-widest text-zinc-500">Past classes</p>
          <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {paginatedPastClasses.length === 0 ? <p className="text-sm text-zinc-500">No past live classes.</p> : paginatedPastClasses.map(renderClassCard)}
          </div>
          {pastClasses.length > CLASSES_PER_PAGE && (
            <nav className="mt-8 flex items-center justify-center gap-2" aria-label="Past live classes pagination">
              <button type="button" disabled={safePastPage === 1} onClick={() => setPastPage((current) => Math.max(1, current - 1))} className="rounded-lg border border-zinc-200 px-3 py-2 text-xs font-bold text-zinc-600 disabled:cursor-not-allowed disabled:opacity-40">Previous</button>
              {Array.from({ length: pastTotalPages }, (_, index) => index + 1).map((pageNumber) => <button key={pageNumber} type="button" onClick={() => setPastPage(pageNumber)} className={`h-9 w-9 rounded-lg text-xs font-black ${safePastPage === pageNumber ? 'bg-zinc-950 text-white' : 'text-zinc-600 hover:bg-zinc-100'}`}>{pageNumber}</button>)}
              <button type="button" disabled={safePastPage === pastTotalPages} onClick={() => setPastPage((current) => Math.min(pastTotalPages, current + 1))} className="rounded-lg border border-zinc-200 px-3 py-2 text-xs font-bold text-zinc-600 disabled:cursor-not-allowed disabled:opacity-40">Next</button>
            </nav>
          )}
        </div>
      )}

      {expiredClass && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-zinc-950/60 px-4" role="dialog" aria-modal="true" aria-labelledby="class-ended-title">
          <div className="w-full max-w-sm rounded-2xl bg-white p-7 text-center shadow-2xl">
            <p className="text-xs font-black uppercase tracking-widest text-red-600">Class ended</p>
            <h3 id="class-ended-title" className="mt-2 text-2xl font-black text-zinc-950">This live class has ended</h3>
            <p className="mt-3 text-sm leading-6 text-zinc-600"><strong>{expiredClass.name}</strong> ended at {localDateTimeFormatter.format(new Date(expiredClass.end_date))} in your local timezone. Refresh the page to see it in Past Classes.</p>
            <button type="button" onClick={() => setExpiredClass(null)} className="mt-6 rounded-xl bg-zinc-950 px-5 py-3 text-xs font-black uppercase tracking-widest text-white">Close</button>
          </div>
        </div>
      )}
    </section>
  );
};
